import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageWrapper } from '../../components/layout';
import { OrgSubnav } from '../../components/shell/OrgSubnav';
import { DepartmentViews, DepartmentsTable, DepartmentPeek } from '../../components/organizations-ledger';
import { DepartmentFormDialog } from '../../components/organizations';
import { LedgerDialog, LedgerSheet } from '../../components/ui';
import { useToast } from '../../hooks/useToast';
import {
  DepartmentListParams,
  useDepartmentsPage,
  useDepartmentDetail,
  useDeleteDepartment,
  useTeamDetail,
} from '../../hooks/useOrgQueries';
import { useLedgerListKeys } from '../../hooks/useLedgerListKeys';
import { Department } from '../../types/organization.types';
import { cn } from '../../lib/cn';

export const DepartmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState('');
  // Cross-nav entry points: TeamPeek links here as ?team=<id>, and a company
  // scope can arrive as ?company=<id>. Seed the filters from the URL so the
  // very first fetch is already scoped. There is no team select in the views —
  // the URL param (and its chip below) is the only way a team filter exists.
  const [company, setCompany] = useState<string | 'ALL'>(
    () => new URLSearchParams(location.search).get('company') ?? 'ALL',
  );
  const [teamFilter, setTeamFilter] = useState<string | null>(
    () => new URLSearchParams(location.search).get('team'),
  );
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [peekOpen, setPeekOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [deleting, setDeleting] = useState<Department | null>(null);
  const toast = useToast();

  // Absorb the ?team=/?company= params into filter state, then strip them so
  // refresh, back-navigation, or copied links don't replay a stale filter.
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const teamFromUrl = urlParams.get('team');
    const companyFromUrl = urlParams.get('company');
    if (teamFromUrl == null && companyFromUrl == null) return;
    if (teamFromUrl != null) setTeamFilter(teamFromUrl);
    if (companyFromUrl != null) setCompany(companyFromUrl);
    setPage(1);
    navigate(location.pathname, { replace: true });
  }, [location, navigate]);

  const params: DepartmentListParams = useMemo(
    () => ({ search, company, team: teamFilter, activeOnly, page }),
    [search, company, teamFilter, activeOnly, page],
  );
  const { data, isPending, isError } = useDepartmentsPage(params);
  const rows = useMemo(() => data?.results ?? [], [data]);
  const count = data?.count ?? 0;

  // Names the chip; the raw id fills in while the lookup is in flight. If the
  // lookup fails outright (team deleted mid-session), fall back to a static
  // label rather than pinning a bare UUID on screen.
  const filterTeam = useTeamDetail(teamFilter);
  const teamChipLabel = filterTeam.data
    ? `Team: ${filterTeam.data.name}`
    : filterTeam.isError
      ? 'Team filter'
      : `Team: ${teamFilter}`;

  useEffect(() => {
    if (isError) toast.error('Failed to load departments');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  // Tailwind's lg breakpoint; the peek is a right rail on desktop and a bottom
  // sheet on mobile, and the sheet portals to <body>, so CSS can't gate it.
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const selectedDepartment = rows.find((department) => department.id === selectedId) ?? null;
  // A selected department can leave the current page (filter or page change);
  // fetch it by id so the peek survives.
  const peekFallback = useDepartmentDetail(peekOpen && selectedDepartment == null ? selectedId : null);
  const peekDepartment = selectedDepartment ?? peekFallback.data ?? null;

  const move = useCallback(
    (delta: number) => {
      if (rows.length === 0) return;
      const index = rows.findIndex((department) => department.id === selectedId);
      const from = index < 0 ? -delta : index;
      const next = Math.min(rows.length - 1, Math.max(0, from + delta));
      setSelectedId(rows[next].id);
    },
    [rows, selectedId],
  );
  useLedgerListKeys({
    enabled: !formOpen && deleting == null,
    onDown: () => move(1),
    onUp: () => move(-1),
    onPeek: () => setPeekOpen(selectedId != null),
    onClose: () => setPeekOpen(false),
  });

  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };
  const changeCompany = (value: string | 'ALL') => {
    setCompany(value);
    // A team belongs to exactly one company, so any cross-nav team filter is
    // stale the moment the company scope changes — keeping both would send
    // team=X&team__company=Y and guarantee an empty table under a live chip.
    setTeamFilter(null);
    setPage(1);
  };
  const changeActiveOnly = (value: boolean) => {
    setActiveOnly(value);
    setPage(1);
  };
  const clearTeamFilter = () => {
    setTeamFilter(null);
    setPage(1);
  };
  const resetView = () => {
    setSearch('');
    setCompany('ALL');
    setActiveOnly(false);
    setTeamFilter(null);
    setPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = () => {
    if (!peekDepartment) return;
    setEditing(peekDepartment);
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  // Runs before the dialog's own close path, so `editing` still tells create
  // and update apart here.
  const handleFormSuccess = () => {
    toast.success(editing ? 'Department updated' : 'Department created');
  };

  const deleteDepartment = useDeleteDepartment();
  const confirmDelete = () => {
    if (!deleting) return;
    const target = deleting;
    // Close synchronously so a second click can't queue a duplicate delete.
    setDeleting(null);
    deleteDepartment.mutate(target.id, {
      onSuccess: () => {
        // Departments are server-paged and an out-of-range page 404s. The
        // hook's invalidation has already started a doomed refetch of this
        // page by the time this runs; stepping back switches the observer to
        // the valid page key in the same render batch, so that 404 lands on an
        // unobserved cache entry instead of the screen.
        if (rows.length === 1 && page > 1) setPage(page - 1);
        toast.success('Department deleted');
        setPeekOpen(false);
        setSelectedId(null);
      },
      onError: () => {
        toast.error('Delete failed');
      },
    });
  };

  const cancelRef = useRef<HTMLButtonElement>(null);
  const deleteConfirm = (
    <LedgerDialog
      open={deleting != null}
      onOpenChange={(next) => {
        if (!next) setDeleting(null);
      }}
      title="Delete department?"
      description={deleting ? `This permanently removes ${deleting.name} from the register.` : undefined}
      size="sm"
      preventClose={deleteDepartment.isPending}
      initialFocus={cancelRef}
      footer={
        <>
          <button
            type="button"
            ref={cancelRef}
            onClick={() => setDeleting(null)}
            disabled={deleteDepartment.isPending}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={deleteDepartment.isPending}
            className="cursor-pointer bg-sev-critical px-3 py-1.5 text-scale-1 font-semibold text-paper hover:opacity-90 disabled:cursor-default disabled:opacity-60"
          >
            Delete
          </button>
        </>
      }
    >
      {null}
    </LedgerDialog>
  );

  return (
    <PageWrapper
      title="Departments"
      subtitle={isPending ? undefined : `${count} department${count === 1 ? '' : 's'}`}
      noPaper
      maxWidth="xl"
      actions={
        <button
          type="button"
          onClick={openCreate}
          className="cursor-pointer bg-accent px-3 py-1 text-scale-1 font-semibold text-paper hover:bg-accent-hover"
        >
          + Add department
        </button>
      }
    >
      <OrgSubnav />

      <div className="hidden md:block">
        <DepartmentViews
          search={search}
          company={company}
          activeOnly={activeOnly}
          onSearchChange={changeSearch}
          onCompanyChange={changeCompany}
          onActiveOnlyChange={changeActiveOnly}
        />
      </div>
      <div className="flex justify-end md:hidden">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          className="cap-label cursor-pointer"
        >
          Filter <span aria-hidden>▾</span>
        </button>
      </div>
      <LedgerSheet open={sheetOpen} onOpenChange={setSheetOpen} title="Views & search">
        <DepartmentViews
          search={search}
          company={company}
          activeOnly={activeOnly}
          onSearchChange={changeSearch}
          onCompanyChange={(next) => {
            changeCompany(next);
            setSheetOpen(false);
          }}
          onActiveOnlyChange={changeActiveOnly}
        />
      </LedgerSheet>

      {teamFilter && (
        <div className="mt-2">
          <span className="cap-label inline-flex items-baseline gap-2 border border-rule-faint px-2 py-0.5">
            {teamChipLabel}
            <button
              type="button"
              aria-label="Clear team filter"
              onClick={clearTeamFilter}
              className="cursor-pointer hover:!text-ink"
            >
              ✕
            </button>
          </span>
        </div>
      )}

      <div className={cn('mt-1', peekOpen && peekDepartment && isDesktop && 'lg:grid lg:grid-cols-[1fr_340px]')}>
        <div className="min-w-0">
          <DepartmentsTable
            rows={rows}
            count={count}
            page={page}
            isPending={isPending}
            hasNext={data?.next != null}
            hasPrevious={data?.previous != null}
            selectedId={selectedId}
            onSelect={(id) => {
              setSelectedId(id);
              setPeekOpen(true);
            }}
            onPageChange={setPage}
            onResetView={resetView}
          />
        </div>
        {peekOpen && peekDepartment && isDesktop && (
          <DepartmentPeek
            department={peekDepartment}
            onClose={() => setPeekOpen(false)}
            onEdit={openEdit}
            onDelete={setDeleting}
          />
        )}
      </div>

      <LedgerSheet
        open={peekOpen && !isDesktop && peekDepartment != null}
        onOpenChange={(open) => {
          if (!open) setPeekOpen(false);
        }}
        title="Department"
      >
        {peekDepartment && (
          <>
            <DepartmentPeek
              department={peekDepartment}
              onClose={() => setPeekOpen(false)}
              onEdit={openEdit}
              onDelete={setDeleting}
            />
            {/* Nested inside the sheet on purpose: sibling Base UI roots would
                both receive a single Escape and close together. */}
            {!isDesktop && deleteConfirm}
          </>
        )}
      </LedgerSheet>
      {isDesktop && deleteConfirm}

      <DepartmentFormDialog open={formOpen} onClose={closeForm} onSuccess={handleFormSuccess} department={editing} />
    </PageWrapper>
  );
};
